import React from 'react';
import { Button, Modal, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

export interface InvoiceEditorModalProps {
  open: boolean;
  title: string;
  dirty: boolean;
  saving: boolean;
  saveDisabled?: boolean;
  onSave: () => void;
  onClose: () => void;
  readOnly?: boolean;
  children: React.ReactNode;
}

/**
 * Large responsive details modal on the same route. Sticky header/footer with
 * internal scrolling; closing (button, X or ESC) never discards — the page
 * draft (and its unsaved indicator) retains every local edit. Those edits
 * live only in page state: a browser reload loses them. Save closes only on
 * success.
 */
const InvoiceEditorModal: React.FC<InvoiceEditorModalProps> = ({
  open,
  title,
  dirty,
  saving,
  saveDisabled,
  onSave,
  onClose,
  readOnly,
  children,
}) => {
  const { t } = useTranslation(['invoices', 'common']);
  return (
    <Modal
      open={open}
      onCancel={onClose}
      maskClosable={false}
      keyboard
      destroyOnClose={false}
      width={960}
      wrapClassName="invoice-editor-modal-wrap"
      className="invoice-editor-modal"
      title={
        <div className="invoice-editor-modal-head">
          <span className="invoice-editor-modal-title">{title}</span>
          {dirty && !readOnly && (
            <Tag color="gold" data-testid="invoice-modal-unsaved">
              {t('invoices:workflow.summary.unsaved')}
            </Tag>
          )}
        </div>
      }
      footer={
        readOnly
          ? [
              <Button key="close" onClick={onClose}>
                {t('common:close')}
              </Button>,
            ]
          : [
              <Typography.Text key="hint" type="secondary" className="invoice-editor-modal-hint">
                {dirty ? t('invoices:workflow.summary.unsavedHint') : ''}
              </Typography.Text>,
              <Button key="close" onClick={onClose} disabled={saving}>
                {t('common:close')}
              </Button>,
              <Button
                key="save"
                type="primary"
                loading={saving}
                disabled={saveDisabled}
                onClick={onSave}
                data-testid="invoice-modal-save"
              >
                {t('invoices:workflow.page.saveDraft')}
              </Button>,
            ]
      }
    >
      <div className="invoice-editor-modal-body">{children}</div>
    </Modal>
  );
};

export default InvoiceEditorModal;
